import React, { useMemo, useState } from "react";

import { Autocomplete, Button, Stack, TextField, TextFieldProps } from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { NumberInput } from "components/forms/Number-input/NumberInput";
import dayjs from "dayjs";
import { debounce } from "lodash";
import { CharacteristicDataType } from "store/types";

import { currenciesArray } from "../../../../constants";
import { DataItem, UpdateItem } from "./SingeCharForm";

const currencyCharVarName = "Currency";
const vintageCharVarName = "Vintage";

const Inputs: Partial<Record<CharacteristicDataType, React.FC<TextFieldProps>>> = {
  text: TextField,
  number: NumberInput,
  percentage: NumberInput,
  currency: NumberInput
};

interface Props {
  data?: DataItem;
  data_type: CharacteristicDataType;
  characteristicName: string;
  onUpdate: UpdateItem;
  onSubmit: () => void;
  onCancel: () => void;
}

const specificCharacteristicsNames = [currencyCharVarName, vintageCharVarName];

export const SingleData: React.FC<Props> = ({ data, data_type, characteristicName, onUpdate, onSubmit, onCancel }) => {
  const { value, id } = data || {};

  const [localValue, setLocalValue] = useState<string>(value || "");
  const [openDatePicker, setOpenDatePicker] = useState(false);

  const debounceUpdate = useMemo(() => debounce(onUpdate, 500), [onUpdate]);

  const isSpecificCharacteristic = specificCharacteristicsNames.includes(characteristicName);

  const Input = !isSpecificCharacteristic && Inputs[data_type];

  const handleValueChange = (value: string) => {
    setLocalValue(value);
    debounceUpdate({ id, value });
  };

  return (
    <>
      {Input && (
        <Input
          fullWidth
          label={characteristicName}
          size="small"
          value={localValue}
          onChange={(e) => handleValueChange(e.target.value)}
          {...(data_type === "percentage" && { numberInputProps: { suffix: "%", decimalScale: 2 } })}
        />
      )}

      {/* Specific characteristics - "Currency" */}
      {data_type === "currency" && characteristicName === currencyCharVarName && (
        <Autocomplete
          options={currenciesArray}
          value={localValue}
          onChange={(_, option) => handleValueChange(option)}
          disableClearable
          renderInput={(params) => <TextField {...params} />}
        />
      )}
      {/* Specific characteristics - "Vintage" */}
      {data_type === "text" && characteristicName === vintageCharVarName && (
        <DesktopDatePicker
          format={"YYYY"}
          views={["year"]}
          open={openDatePicker}
          onOpen={() => setOpenDatePicker(true)}
          onClose={() => setOpenDatePicker(false)}
          slotProps={{
            popper: {
              sx: {
                ".MuiPickersCalendarHeader-labelContainer": {
                  fontSize: "0.9rem"
                }
              }
            },
            textField: {
              onClick: () => setOpenDatePicker(true),
              error: false,
              sx: {
                marginBottom: 0,
                input: { padding: "8px 6px", fontSize: "16px" }
              }
            }
          }}
          value={value ? dayjs(value) : null}
          onChange={(date) => {
            date && handleValueChange(dayjs(date).format("YYYY"));
          }}
        />
      )}
      <Stack direction="row" justifyContent="flex-end" alignItems="center" gap="10px">
        <Button variant="outlined" onClick={onCancel}>
          Cancel
        </Button>
        <Button disabled={!data} onClick={onSubmit} variant="contained">
          Apply
        </Button>
      </Stack>
    </>
  );
};
